import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { IPageInfo } from '../interfaces/interface';
import { StorageService } from './storage.service';
import { PlatformService } from './platform.service';
import { langsSupported } from '../enums/languages';
import { TranslateService } from '../../shared/modules/translate/translate.service';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class SEOService {
  private lastPageInfo: IPageInfo = undefined as any;
  private get currentUrl() {
    return decodeURI(
      `${environment.front.href}${this.document.location.pathname}`,
    );
  }
  private get origin() {
    return this.document.location.origin;
  }
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private title: Title,
    private meta: Meta,
    private storageService: StorageService,
    private translationService: TranslateService,
    private pt: PlatformService,
    private router: Router,
  ) {
    translationService.onLangChange.subscribe(() => {
      this.setPageSEO(this.lastPageInfo);
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        setTimeout(() => {
          this.addDNSPrefetech();
          this.addCanonical();
          this.addAlternate();
        }, 0);
      }
    });
  }
  async setPageSEO(
    pageInfo: IPageInfo,
  ) {
    if (!pageInfo) return;
    this.lastPageInfo = Object.assign({}, pageInfo);

    var image = this.fixImage(pageInfo);

    let descriptionTranslated = pageInfo.translated ? pageInfo.description : this.translationService.get(
      pageInfo.description as any,
    );
    let titleTranslated = pageInfo.translated ? pageInfo.title : this.translationService.get(pageInfo.title);

    if (descriptionTranslated) {
      this.setPageMeta(titleTranslated, descriptionTranslated);

      this.addTwitterCard(titleTranslated, descriptionTranslated, image || '');

      this.addOpenGraphCard(
        titleTranslated,
        descriptionTranslated,
        image || '',
        pageInfo.isArticle || false,
      );
    }

    // if (pageInfo.isArticle) {
    //  this.setDisallowRobots();
    // } else {
    // this.setAllowRobots();
    // }
    this.setAllowRobots();

    this.addOrgnizationInfo();
    this.addBreadcrumbList();
    this.addSiteNavigationElement();


    this.addService(image, titleTranslated, descriptionTranslated);
    this.addRating(titleTranslated);

  }

  fixImage(pageInfo: IPageInfo) {
    let img = '';
    if (!pageInfo.image) {
      img = `assets/media/images/screenshots/$lang/home-page.png`;
    }

    img = `${pageInfo.image?.replace('$lang', this.storageService.i18n.value)}`;

    if (
      !img.includes(environment.front.href) &&
      !img.includes('https://') &&
      !img.includes('http://')
    ) {
      img = `${environment.front.href}/${img}`;
    }

    return img;
  }
  async setLanguage(lang: string) {
    this.updateMetaProp('og:locale', lang);
  }

  private async setPageMeta(title: string, description: string) {
    this.title.setTitle(title);
    this.updateTag('description', description);
  }

  // async updateTranslation() {
  //   if (this.lastPageInfo) {
  //     this.setPageSEO(this.lastPageInfo);
  //   }
  // }

  private async addTwitterCard(
    title: string,
    description: string,
    image: string,
  ) {
    this.updateMetaProp('twitter:title', title);
    this.updateMetaProp('twitter:description', description);
    if (image) {
      this.updateMetaProp('twitter:image', image);
    }
    this.updateMetaProp('twitter:card', 'summary_large_image');
    this.updateMetaProp('twitter:domain', this.currentUrl);
  }

  private async addOpenGraphCard(
    title: string,
    description: string,
    image: string,
    isArticle: boolean,
  ) {
    this.updateMetaProp('og:title', title);
    this.updateMetaProp('og:description', description);
    if (image) {
      this.updateMetaProp('og:image', image);
      this.updateMetaProp('og:image:alt', title);
    }
    this.updateMetaProp('og:url', this.currentUrl);

    if (isArticle) {
      this.updateMetaProp('og:type', 'article');
    }
  }

  private async addDNSPrefetech() {
    this.cleanElements('[urlDns]');
    this.document.head.insertAdjacentHTML(
      'beforeend',
      `
<link urlDns href="${this.origin}" rel="preconnect" />


`,
    );
  }

  async addCanonical() {
    this.cleanElements('link[rel="canonical"]');
    this.document.head.insertAdjacentHTML(
      'beforeend',
      `<link href="${this.document.location.origin + this.router.url
      }" rel="canonical" />`,
    );
  }

  private async cleanElements(selector: string) {
    let allow = true;
    while (allow) {
      const elm = this.document.querySelector(selector);
      if (elm) {
        elm.remove();
      } else {
        allow = false;
      }
    }
  }

  private async updateTag(name: string, content: string) {
    this.meta.updateTag({ name, content });
  }

  private async updateMetaProp(name: string, content: string) {
    this.meta.updateTag({ property: name, content });
  }

  /** Article Landing Page  */
  async setAllowRobots() {
    this.removeRobots();
    this.meta.addTag({
      name: 'robots',
      content: 'all,noodp',
    });
  }

  /** Any page you done't need to shoe in search engine  */
  async setDisallowRobots() {
    this.removeRobots();
    this.meta.addTag({
      name: 'robots',
      content: 'noindex,noodp',
    });
  }
  async removeRobots() {
    this.meta.removeTag('name="robots"');
  }
  convertPathToSEOPath(path: string, name: string) {
    const formattedName = name
      .trim()
      //.replace(/\s+/g, '-').trim();
      .toLowerCase()
      .replace(/[^a-z0-9\u0621-\u064A\u0660-\u0669]+/g, '-') //  convert any un valid characters to "-"
      .replace(/^-+|-+$/g, '');
    return `${path}/${formattedName}`;
  }

  async addAlternate() {
    this.cleanElements('link[rel="alternate"]');
    const links = this.document.location.pathname
      .split('/')
      .filter((c) => !langsSupported.some((v) => v == c) && c != '');
    var htmlTags = langsSupported.map(
      (lang) =>
        `<link rel="alternate" hreflang="${lang}" href="${environment.front.href +
        '/' +
        lang +
        (links.length ? '/' + links.join('/') : '')
        }"/>`,
    );

    htmlTags.push(
      `<link rel="alternate" hreflang="x-default" href="${environment.front.href +
      '/en' +
      (links.length ? '/' + links.join('/') : '')
      }"/>`,
    );
    this.document.head.insertAdjacentHTML('beforeend', htmlTags.join('\n'));
  }


  private async addOrgnizationInfo() {
    let json = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": this.translationService.get('Posty 5'),
      "url": `${environment.front.href}/${this.translationService.lastLang}`,
      "logo": `${environment.front.href}/icons/icon-512x512.png`,
      "sameAs": [
        "https://www.facebook.com/Posty5App",
        "https://www.youtube.com/@posty5",
      ]
    };
    this.injectSchema("OrganizationSchema", json);
  }
  private async addSiteNavigationElement() {
    let siteNavigationElements = [
      {
        name: "Partner Earnings",
        rootPath: "partner-earnings",
      },
      {
        name: "URL Shortener",
        rootPath: "url-shortener",
      },
      {
        name: "QR Code Generator",
        rootPath: "qr-code-generator",
      },

      {
        name: "SEO Extractor",
        rootPath: "seo-tags-extractor",
      },
      {
        name: "HTML hosting",
        rootPath: "html-hosting",
      },
      {
        name: "About Us",
        rootPath: "about-us",
      }
    ]
    let json = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": siteNavigationElements.map((elm, index) => {
        return {
          "@type": "SiteNavigationElement",
          "position": index + 1,
          "name": this.translationService.get(elm.name),
          "url": `${environment.front.href}/${this.translationService.lastLang}/${elm.rootPath}`
        }
      })
    };
    this.injectSchema("SiteNavigationElement Schema", json);
  }

  private async addBreadcrumbList() {
    let json = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": this.translationService.get('Home'),
          "item": `${environment.front.href}/${this.translationService.lastLang}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "QR Code Generator",
          "item": this.currentUrl
        }
      ]
    };
    this.injectSchema("BreadcrumbListSchema", json);

  }

  private async addService(image: any, title: any, description: any) {
    if (!this.lastPageInfo.isTool) {
      this.cleanElements(`#ServiceSchema`);
      return
    }
    let json = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": title,
      "image": image,
      "description": description,
      "provider": {
        "@type": "Organization",
        "name": this.translationService.get("Posty 5")
      }
    }
    this.injectSchema("ServiceSchema", json);
  }

  private async addRating(title: any) {
    if (!this.lastPageInfo.isTool) {
      this.cleanElements(`#RatingSchema`);
      return
    }
    let json =
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": title,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "125"
      }
    }
    this.injectSchema("RatingSchema", json);

  }

  async addChromeExe(name: string, link: string) {
    let json =
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": this.translationService.get(name),
      "operatingSystem": "All",
      "browserRequirements": "Requires Google Chrome",
      "applicationCategory": "BrowserExtension",
      "downloadUrl": link,
      "offers": {
        "@type": "Offer",
        "price": "0.00",
        "priceCurrency": "USD"
      }
    }
    this.injectSchema("RatingSchema", json);
  }


  private async injectSchema(id: string, json: any) {
    this.cleanElements(`#${id}`);
    this.document.body.insertAdjacentHTML("beforeend", `<script type="application/ld+json" id="${id}">${JSON.stringify(json)}</script>`);
  }

}
